import Card from "@/components/card"
import ListButton from "@/components/list-button"
import ListInput from "@/components/list-input"
import ListItem from "@/components/list-item"
import Space from "@/components/space"
import Modal from "@/components/modal"
import Button from "@/components/button"
import { useAppDispatch, useAppSelector } from "@/core/root-reducer"
import {
  faChevronUp,
  faChevronDown,
  faGripVertical,
  faTrash,
  faInfoCircle,
  faList,
  faTimes,
  faExclamationTriangle,
  faCog
} from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { t } from "i18next"
import { useState } from "react"
import { DragDropContext, Droppable, Draggable, DropResult } from "react-beautiful-dnd"
import Tooltip from "@/components/tooltip"
import ToggleButton from "@/components/toggle-button"
import organizationStore from "@/features/adm/entities/organization/model/organization.store"

type Props = {
  onDirty: () => void
}

export default function RiskOrganizationAttributesConfig({ onDirty }: Props) {
  const dispatch = useAppDispatch()

  const [isOpen, setIsOpen] = useState(false)
  const [deleteConfirmModal, setDeleteConfirmModal] = useState<string | null>(null)
  const { riskAttributes, optionalRiskAttributes = [] } = useAppSelector(organizationStore.selectors.draft)

  const toggleOptional = (key: string) => {
    const updated = optionalRiskAttributes.includes(key)
      ? optionalRiskAttributes.filter((k) => k !== key)
      : [...optionalRiskAttributes, key]
    dispatch(organizationStore.actions.edit({ key: "optionalRiskAttributes", value: updated }))
    onDirty()
  }

  const updateAttributes = (newAttributes: string[]) => {
    dispatch(
      organizationStore.actions.edit({
        key: "riskAttributes",
        value: newAttributes
      })
    )
    onDirty()
  }

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return
    const reordered = Array.from({ riskAttributes }.riskAttributes)
    const [moved] = reordered.splice(result.source.index, 1)
    reordered.splice(result.destination.index, 0, moved)
    updateAttributes(reordered)
  }

  const handleEdit = (oldKey: string, newKey: string) => {
    const updated = { riskAttributes }.riskAttributes.map((attr) => (attr === oldKey ? newKey : attr))
    updateAttributes(updated)
  }

  const handleRemove = (key: string) => {
    setDeleteConfirmModal(key)
  }

  const handleConfirmDelete = () => {
    if (!deleteConfirmModal) return
    updateAttributes(riskAttributes.filter((attr) => attr !== deleteConfirmModal))
    if (optionalRiskAttributes.includes(deleteConfirmModal)) {
      dispatch(organizationStore.actions.edit({
        key: "optionalRiskAttributes",
        value: optionalRiskAttributes.filter((k) => k !== deleteConfirmModal)
      }))
    }
    setDeleteConfirmModal(null)
  }

  const handleAdd = () => {
    updateAttributes([...{ riskAttributes }.riskAttributes, t("utils_new")])
  }

  return (
    <Card className="cursor-pointer" onClick={() => setIsOpen(!isOpen)}>
      <div className={`flex justify-between items-center min-h-14 ${isOpen ? "mb-4" : ""}`}>
        <div className="flex flex-col justify-center">
          <div className="flex items-center gap-3">
            <FontAwesomeIcon icon={faCog} className="grey-t text-lg" />
            <span className="text-xl font-bold">{t("Configuration des attributs")}</span>
            <Tooltip
              text={t(
                "Le 1er attribut définit la famille de regroupement (ex : « Famille de risque »).\nLe 2ème attribut est le nom du risque.\nLes suivants sont libres (ex : situation dangereuse, effets, mesures…)."
              )}
              position="right"
              width={280}>
              <FontAwesomeIcon icon={faInfoCircle} className="grey-t cursor-help" />
            </Tooltip>
          </div>
        </div>
        <Space />
        <span className={`grey-t mr-1 ${isOpen ? "visible" : "invisible"}`}>{t("(Déplacer pour réordonner)")}</span>
        <FontAwesomeIcon
          icon={isOpen ? faChevronUp : faChevronDown}
          className="grey-t cursor-pointer ml-2"
          onClick={() => setIsOpen((v) => !v)}
        />
      </div>
      {/* Content */}
      {isOpen && (
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="establishment-attributes">
            {(provided) => (
              <div
                className={`${riskAttributes.length === 0 ? "flex" : "flex-col"} w-full items-center`}
                ref={provided.innerRef}
                {...provided.droppableProps}>
                {riskAttributes.length === 0 ? (
                  <div className="grey-t font-bold flex-1">{t("Vous n'avez créé aucun attributs pour le moment.")}</div>
                ) : (
                  riskAttributes.map((attr, index) => (
                    <Draggable key={attr} draggableId={attr} index={index}>
                      {(provided) => (
                        <div ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}>
                          <ListItem className="items-center">
                            <FontAwesomeIcon icon={faGripVertical} className="text-gray-400 my-auto mr-2 cursor-grab" />
                            <ListInput
                              value={attr}
                              onEditEnd={(val: string) => handleEdit(attr, val)}
                              hint={t("Appuyez sur Entrée pour valider ou Échap pour annuler")}
                            />
                            <Space />
                            <span className="text-sm grey-t mr-4">
                              {!optionalRiskAttributes.includes(attr) ? t("Obligatoire") : t("Optionnel")}
                            </span>
                            <div onClick={(e) => e.stopPropagation()} className="mr-2">
                              <Tooltip
                                text={t("Si activé, ce champ doit être renseigné pour que la fiche soit considérée comme complète")}
                                position="top"
                                width={220}>
                                <ToggleButton
                                  isActive={!optionalRiskAttributes.includes(attr)}
                                  onToogle={() => toggleOptional(attr)}
                                />
                              </Tooltip>
                            </div>
                            <ListButton icon={faTrash} text={t("utils_delete")} onClick={() => handleRemove(attr)} />
                          </ListItem>
                        </div>
                      )}
                    </Draggable>
                  ))
                )}
                <span>{provided.placeholder}</span>
                <div className="text-right">
                  <ListButton plus icon={faList} text={t("Ajouter un attribut")} onClick={handleAdd} />
                </div>
              </div>
            )}
          </Droppable>
        </DragDropContext>
      )}

      {deleteConfirmModal && (
        <Modal
          title={t("Supprimer un attribut")}
          footer={
            <div className="flex w-full m-[-5px]">
              <Space />
              <Button onClick={() => setDeleteConfirmModal(null)} className="med" icon={faTimes} isWithBorder>
                {t("utils_cancel")}
              </Button>
              <Button onClick={handleConfirmDelete} className="primary" icon={faTrash}>
                {t("utils_delete")}
              </Button>
            </div>
          }>
          <div className="flex flex-col gap-4">
            <p className="font-semibold text-base">
              {t(`Êtes-vous sûr de vouloir supprimer l'attribut « ${deleteConfirmModal} » ?`)}
            </p>
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center gap-3">
                <FontAwesomeIcon icon={faExclamationTriangle} className="red-t flex-shrink-0 text-lg" />
                <p className="text-sm red-t">
                  <strong>{t("Attention :")}</strong>{" "}
                  {t(
                    "Cette action est irréversible. Tous les risques associés à cet attribut perdront les données saisies dans ce champ. Cette information sera supprimée de façon permanente pour tous les risques de votre organisation."
                  )}
                </p>
              </div>
            </div>
            <p className="text-sm grey-t">
              <strong>{t("Note :")}</strong>{" "}
              {t(
                "Les modifications seront appliquées uniquement après avoir cliqué sur le bouton Sauvegarder. Vous pouvez annuler avant la sauvegarde pour revenir à l'état initial."
              )}
            </p>
          </div>
        </Modal>
      )}
    </Card>
  )
}
